import { DateField } from "../../components/DateField";
import { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  Field,
  PersonCell,
  SelectX,
  StatusPill,
  Textarea,
  Avatar,
} from "@guestpilot/ui-kit";
import { useGet } from "../../api/hooks";
import type { PersonRef, Schema } from "../../api/types";
import { PersonPicker } from "../../components/PersonPicker";
import { Flag, RemovalConsequences, type Participant, type Reason } from "../../components/Participants";
import { useI18n } from "../../i18n";
import { useSession } from "../../session";

export type Decision = Schema<"Decision">;
type Kind = Decision["decision"];
type Settings = Schema<"Settings">;
type Preview = {
  outcome: string;
  executeAfterHours?: number;
  mode?: string;
  otherControlledTeams?: Schema<"TeamRef">[];
  activeExceptionWillBeRevoked?: boolean;
  warnsOutsideScope?: boolean;
};

const today = () => new Date().toISOString().slice(0, 10);

/** Client-side check of the per-decision rules (BR-081…BR-086); the API validates again and its field errors are shown (BR-211). */
export function validateDecision(
  kind: Kind,
  f: {
    sponsor: string | null;
    expiry: string;
    reason: string;
    reasonText: string;
    until: string;
    currentSponsor: string | null;
    currentExpiry: string | null;
  },
  maxAccessDays: number | null,
  now = today(),
): Record<string, string> {
  const e: Record<string, string> = {};
  const futureExpiry = (): void => {
    if (!f.expiry) e["expiryDate"] = "required";
    else if (f.expiry <= now) e["expiryDate"] = "future";
    else if (
      maxAccessDays &&
      f.expiry > new Date(Date.parse(now) + maxAccessDays * 86_400_000).toISOString().slice(0, 10)
    )
      e["expiryDate"] = "max";
  };
  if (kind === "keep") {
    if (!f.sponsor) e["sponsorPersonId"] = "required";
    futureExpiry();
    if (!f.reason) e["accessReasonId"] = "required";
  }
  if (kind === "extend") {
    futureExpiry();
    if (!e["expiryDate"] && f.currentExpiry && f.expiry <= f.currentExpiry) e["expiryDate"] = "later";
    if (!f.reason) e["accessReasonId"] = "required";
  }
  if (kind === "remove" && !f.reasonText.trim()) e["reasonText"] = "required";
  if (kind === "exception") {
    if (!f.reasonText.trim()) e["reasonText"] = "required";
    if (!f.until || f.until <= now) e["exceptionValidUntil"] = f.until ? "future" : "required";
  }
  if (kind === "transfer_sponsor" && (!f.sponsor || f.sponsor === f.currentSponsor))
    e["sponsorPersonId"] = f.sponsor ? "different" : "required";
  return e;
}


/** An Admin's own Remove decision counts as validation (BR-210): it is scheduled like a delegated request, it does not wait for another Admin. */
function removeOutcome(outcome: string | undefined, hours: number | undefined, isAdmin: boolean): string {
  if (outcome !== "needs_admin_validation" || !isAdmin) return outcome ?? "record_only";
  return (hours ?? 0) > 0 ? "scheduled_after_delay" : "executes_immediately";
}

type Ask = "remove" | "exception" | "transfer";

/**
 * One guest's decision form (team-tab.md Detail, D-191). The page holds what a Keep needs (sponsor, expiry, reason, notes).
 * Keep covers extending too: a later expiry is saved as an Extend (BR-082), the same or an earlier one as a Keep. The decisions that need
 * more than that ask for it in a pop-up when chosen: Request removal (reason and what will happen), Exception (reason and end date) and
 * Transfer sponsor (the new sponsor, offered only when the guest has a sponsor).
 */
export function DecisionForm({
  participant,
  itemVersion,
  participantVersion,
  decided,
  serverErrors,
  busy,
  onSubmit,
  gateReason,
}: {
  participant: Participant;
  itemVersion?: number | undefined;
  participantVersion?: number | undefined;
  decided?: string | undefined;
  serverErrors?: Record<string, string> | undefined;
  busy?: boolean | undefined;
  onSubmit: (d: Decision & { participantVersion?: number }) => void;
  gateReason?: string | undefined;
}) {
  const { t } = useI18n();
  const s = useSession();
  const reasons = useGet<Reason[]>("/api/v1/access-reasons");
  const settings = useGet<Settings>("/api/v1/settings", undefined, { enabled: s.can("SETTINGS_READ") });
  const [sponsor, setSponsor] = useState<PersonRef | null>(participant.sponsor ?? null);
  const [newSponsor, setNewSponsor] = useState<PersonRef | null>(null);
  const [expiry, setExpiry] = useState(participant.expiryDate ?? "");
  const [reason, setReason] = useState(participant.reason?.id ?? "");
  const [reasonText, setReasonText] = useState("");
  const [comment, setComment] = useState("");
  const [until, setUntil] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [kind, setKind] = useState<Kind | null>(null);
  const [touched, setTouched] = useState(false);
  const [ask, setAsk] = useState<Ask | null>(null);
  const preview = useGet<Preview>(`/api/v1/participants/${participant.id}/removal-preview`, undefined, {
    enabled: ask === "remove",
  });
  const maxAccess = settings.data?.maxAccessDays ?? null;
  const values = {
    sponsor: kind === "transfer_sponsor" ? (newSponsor?.id ?? null) : (sponsor?.id ?? null),
    expiry,
    reason,
    reasonText,
    until,
    currentSponsor: participant.sponsor?.id ?? null,
    currentExpiry: participant.expiryDate ?? null,
  };
  const local = kind ? validateDecision(kind, values, maxAccess) : {};
  const err = (field: string): string | undefined => {
    const server = serverErrors?.[field];
    if (server) return server;
    const code = touched ? local[field] : undefined;
    return code ? t(`ui:decision.err.${code}`, { days: maxAccess ?? 0 }) : undefined;
  };
  const reasonOptions = [
    { value: "", label: t("ui:users.noReason") },
    ...(reasons.data ?? [])
      .filter((r) => !r.retired || r.id === participant.reason?.id)
      .map((r) => ({ value: r.id ?? "", label: r.label ?? "" })),
  ];
  const outcome = removeOutcome(preview.data?.outcome, preview.data?.executeAfterHours, s.can("REMOVAL_VALIDATE"));
  const irreversible = outcome === "executes_immediately" || outcome === "scheduled_after_delay";
  const name = participant.displayName || participant.email;

  const open = (a: Ask) => {
    setKind(a === "transfer" ? "transfer_sponsor" : a);
    setTouched(false);
    setConfirmed(false);
    setAsk(a);
  };
  const close = () => {
    setAsk(null);
    setKind(null);
    setTouched(false);
  };

  /** Checks the rules of the chosen decision, then sends it. Keep saves as Extend when the new expiry is later than the current one. */
  const submit = (k: Kind) => {
    setKind(k);
    setTouched(true);
    const errs = validateDecision(k, values, maxAccess);
    if (Object.keys(errs).length > 0) return;
    const saved: Kind =
      k === "keep" && participant.expiryDate && expiry > participant.expiryDate ? "extend" : k;
    onSubmit({
      decision: saved,
      itemVersion: itemVersion ?? 0,
      ...(participantVersion !== undefined ? { participantVersion } : {}),
      ...(k === "keep" ? { sponsorPersonId: sponsor?.id, expiryDate: expiry, accessReasonId: reason } : {}),
      ...(k === "transfer_sponsor" ? { sponsorPersonId: newSponsor?.id } : {}),
      ...(k === "remove" || k === "exception" ? { reasonText } : {}),
      ...(k === "exception" ? { exceptionValidUntil: until } : {}),
      ...(comment ? { comment } : {}),
      ...(k === "remove" && irreversible ? { confirmIrreversible: true } : {}),
    });
  };
  const waiting = gateReason ?? (busy ? t("common:loading") : undefined);

  return (
    <div className="gp-stack" style={{ gap: "var(--space-16)" }}>
      <div className="gp-row">
        <Avatar name={name} size={40} />
        <div className="gp-person-text">
          <span className="t-title-sm">{name}</span>
          <span className="t-body gp-muted">
            {participant.email} · {participant.domain}
          </span>
        </div>
        {decided ? <StatusPill domain="outcome" value={decided} label={t(`ui:outcome.${decided}`)} /> : null}
      </div>
      <div className="gp-row" aria-label={t("ui:home.filterRisk")}>
        {(participant.risks ?? [])
          .filter((r) => r.type && !r.excepted)
          .map((r) => (
            <Flag key={r.type} risk={r.type!} />
          ))}
        {participant.suggestedSponsor && !participant.sponsor ? (
          <span className="t-body-sm gp-muted">
            {t("ui:users.suggested", { name: participant.suggestedSponsor.displayName })}
          </span>
        ) : null}
      </div>
      {participant.channels && participant.channels.length > 0 ? (
        <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
          {t("ui:decision.channels", { list: participant.channels.map((c) => c.name).join(", ") })}
        </p>
      ) : null}
      <Field label={t("ui:users.col.sponsor")} error={kind === "keep" ? err("sponsorPersonId") : undefined}>
        <PersonPicker label={t("ui:users.col.sponsor")} value={sponsor} onChange={setSponsor} />
      </Field>
      <div className="gp-grid-2">
        <Field
          label={t("ui:users.col.expiry")}
          error={kind === "keep" ? err("expiryDate") : undefined}
          help={maxAccess ? t("ui:decision.maxAccess", { days: maxAccess }) : undefined}
        >
          <DateField value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </Field>
        <Field label={t("ui:users.col.reason")} error={kind === "keep" ? err("accessReasonId") : undefined}>
          <SelectX options={reasonOptions} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <Field label={t("ui:users.notes")}>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
      </Field>
      <div className="gp-decisions" role="group" aria-label={t("ui:decision.group")}>
        <Button variant="primary" disabledReason={waiting} onClick={() => submit("keep")}>
          {t("ui:decision.keep")}
        </Button>
        <Button variant="danger" disabledReason={waiting} onClick={() => open("remove")}>
          {t("ui:decision.remove")}
        </Button>
        <Button disabledReason={waiting} onClick={() => open("exception")}>
          {t("ui:decision.exception")}
        </Button>
        {participant.sponsor ? (
          <Button disabledReason={waiting} onClick={() => open("transfer")}>
            {t("ui:decision.transfer_sponsor")}
          </Button>
        ) : null}
      </div>
      {participant.sponsor ? (
        <PersonCell
          name={participant.sponsor.displayName}
          sub={t("ui:decision.currentSponsor")}
          avatar={false}
        />
      ) : null}

      <Dialog
        open={ask === "remove"}
        onClose={close}
        title={t("ui:removal.title", { name })}
        description={t("ui:removal.lead")}
        footer={
          <>
            <Button onClick={close}>{t("common:cancel")}</Button>
            <Button
              variant={irreversible ? "danger" : "primary"}
              disabledReason={
                busy
                  ? t("common:loading")
                  : !preview.data
                    ? t("common:loading")
                    : irreversible && !confirmed
                      ? t("ui:removal.confirmRequired")
                      : undefined
              }
              onClick={() => submit("remove")}
            >
              {t(`ui:removal.action.${outcome}`)}
            </Button>
          </>
        }
      >
        {preview.data ? <RemovalConsequences preview={preview.data} outcome={outcome} /> : null}
        <Field label={t("ui:removal.reason")} error={err("reasonText")}>
          <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
        </Field>
        {irreversible ? (
          <Checkbox checked={confirmed} onChange={setConfirmed} label={t("ui:removal.irreversible")} />
        ) : null}
      </Dialog>

      <Dialog
        open={ask === "exception"}
        onClose={close}
        title={t("ui:decision.exceptionTitle", { name })}
        description={t("ui:decision.exceptionLead")}
        footer={
          <>
            <Button onClick={close}>{t("common:cancel")}</Button>
            <Button
              variant="primary"
              disabledReason={busy ? t("common:loading") : undefined}
              onClick={() => submit("exception")}
            >
              {t("ui:decision.exceptionConfirm")}
            </Button>
          </>
        }
      >
        <Field label={t("ui:decision.exceptionReason")} error={err("reasonText")}>
          <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
        </Field>
        <Field label={t("ui:decision.until")} error={err("exceptionValidUntil")} help={t("ui:decision.untilHelp")}>
          <DateField value={until} onChange={(e) => setUntil(e.target.value)} />
        </Field>
      </Dialog>

      <Dialog
        open={ask === "transfer"}
        onClose={close}
        title={t("ui:decision.transferTitle", { name })}
        description={t("ui:decision.transferLead")}
        footer={
          <>
            <Button onClick={close}>{t("common:cancel")}</Button>
            <Button
              variant="primary"
              disabledReason={busy ? t("common:loading") : undefined}
              onClick={() => submit("transfer_sponsor")}
            >
              {t("ui:decision.transferConfirm")}
            </Button>
          </>
        }
      >
        <Field label={t("ui:decision.newSponsor")} error={err("sponsorPersonId")}>
          <PersonPicker label={t("ui:decision.newSponsor")} value={newSponsor} onChange={setNewSponsor} />
        </Field>
      </Dialog>
    </div>
  );
}
